<script setup lang="ts">
/**
 * @fileoverview Provider component for platform-level theming
 * Applies platform branding from developerStore to Vuetify theme.
 *
 * This runs BEFORE tenant-specific theming and ensures platform pages
 * (PinGuard, login, landing) use configured platform colors.
 *
 * @module components/common/PlatformThemeProvider
 * @see src/config/brandingDefaults.ts for color source of truth
 */
import { watch, onMounted, computed } from 'vue'
import { useTheme } from 'vuetify'
import { useDeveloperStore } from '@/stores/developer'
import { COLOR_DEFAULTS } from '@/config/brandingDefaults'

const developerStore = useDeveloperStore()
const vuetifyTheme = useTheme()

const brandConfig = computed(() => developerStore.brandConfig)

/**
 * Default brand colors from centralized config
 */
const defaultColors = {
  primary: COLOR_DEFAULTS.primary,
  secondary: COLOR_DEFAULTS.secondary,
  accent: COLOR_DEFAULTS.accent,
  info: COLOR_DEFAULTS.info,
  error: COLOR_DEFAULTS.error,
  warning: COLOR_DEFAULTS.warning,
  success: COLOR_DEFAULTS.success,
  background: COLOR_DEFAULTS.background,
  surface: COLOR_DEFAULTS.surface,
}

/**
 * Apply platform branding to Vuetify theme
 */
function applyTheme() {
  const colors = brandConfig.value?.colors ?? defaultColors

  // Update Vuetify theme colors
  vuetifyTheme.themes.value.light.colors = {
    ...vuetifyTheme.themes.value.light.colors,
    primary: colors.primary || defaultColors.primary,
    secondary: colors.secondary || defaultColors.secondary,
    error: colors.error || defaultColors.error,
    warning: colors.warning || defaultColors.warning,
    success: colors.success || defaultColors.success,
    info: colors.info || defaultColors.info,
    background: colors.background || defaultColors.background,
    surface: colors.surface || defaultColors.surface
  }
}

// Watch for branding changes and apply theme
watch(brandConfig, () => {
  applyTheme()
}, { immediate: true, deep: true })

onMounted(() => {
  if (brandConfig.value) {
    applyTheme()
  }
})
</script>

<template>
  <slot />
</template>
